import { ConfigProvider, Flex, Modal, Spin, Typography } from "antd";
import { useState } from "react";
import { useAuth } from "../../context/AuthContext";
import useCameras from "../../hooks/useCameras";
import useCollectionsWithoutRevenue from "../../hooks/useCollectionsWithoutRevenue";
import useExistsDaysWithoutSchedule from "../../hooks/useExistsDaysWithoutSchedule";
import useExistsZoneWithoutCollection from "../../hooks/useExistsZoneWithoutCollection";
import useFlowDetectionsLastRecord from "../../hooks/useFlowDetectionsLastRecord";
import useFlowDetectionsProcessData from "../../hooks/useFlowDetectionsProcessData";
import useHomeRevenueKPIsData from "../../hooks/useHomeRevenueKPIsData";
import useUnlinkedCollections from "../../hooks/useUnlinkedCollections";
import useZones from "../../hooks/useZones";
import HomeDashboard from "./HomeDashboard";
import Warning from "./Warning";

const { Title, Text, Link } = Typography;
const warnings = [
  {
    about: "câmera",
    link: "/zones_manager",
    title: "Nenhuma câmera cadastrada",
    description:
      "Adicionar uma câmera é o primeiro passo para utilizar a plataforma. Sem o cadastro das câmeras, a utilização da plataforma é inviabilizada.",
  },
  {
    about: "zona",
    link: "/zones_manager",
    title: "Nenhuma zona cadastrada",
    description:
      "Adicione uma zona para utilizar a plataforma. As zonas são necessárias para identificar o fluxo de pessoas em regiões específicas de uma câmera.",
  },
  {
    about: "coleção",
    link: "/events_manager",
    title: "Zonas sem coleções",
    description:
      "Adicione uma coleção a uma zona para utilizar a plataforma. A relação é necessária para cruzar dados de faturamento com o fluxo de pessoas.",
  },
  {
    about: "horário de funcionamento",
    link: "/opening_hours",
    title: "Horário de funcionamento não cadastrado",
    description:
      "Adicione um horário de funcionamento para utilizar a plataforma. A relação é necessária para cruzar dados de faturamento com o fluxo de pessoas.",
  },
  {
    about: "coleção a uma zona da loja",
    link: "/events_manager",
    title: "Coleções faturadas não vinculadas a zonas",
    description:
      "Existem coleções sendo faturadas nos últimos 7 dias que não estão vinculadas a nenhuma zona. Vincule-as para melhor análise do fluxo de pessoas.",
  },
  {
    about: "coleção a uma zona da loja",
    link: "/events_manager",
    title: "Coleções não faturadas vinculadas a zonas",
    description:
      "Existem coleções que não estão sendo faturadas nos últimos 15 dias que ainda estão relacionadas a zonas. Verifique se as coleções estão corretas e se estão sendo faturadas.",
  },
];

export default function Home() {
  const {
    data: cameras,
    camerasCount,
    isLoading: isLoadingCamerasCount,
  } = useCameras();
  const { count: zonesCount, isLoading: isLoadingZonesCount } = useZones({
    active_only: true,
  });

  const {
    data: existsZoneWithoutCollection,
    isLoading: isLoadingExistsZoneWithoutCollection,
  } = useExistsZoneWithoutCollection();
  const {
    data: existsDaysWithoutSchedule,
    isLoading: isLoadingExistsDaysWithoutSchedule,
  } = useExistsDaysWithoutSchedule();

  const { data: lastRecord, isPending: isLastRecordPending } =
    useFlowDetectionsLastRecord({ enabled: true });

  const {
    setIsZeroPhaseTutorialOpen,
    shouldShowTutorialBool,
    setShouldShowTutorialBool,
    postShouldNeverShowTutorial,
  } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState("yesterday");

  const hasCameras = camerasCount && camerasCount > 0;
  const hasZones = hasCameras && zonesCount && zonesCount > 0;

  const { data: unlinkedCollections, isLoading: isLoadingUnlinkedCollections } =
    useUnlinkedCollections(
      Boolean(hasZones && hasCameras && !existsZoneWithoutCollection)
    );

  const {
    data: collectionsWithoutRevenue,
    isLoading: isLoadingCollectionsWithoutRevenue,
  } = useCollectionsWithoutRevenue(
    Boolean(hasZones && hasCameras && !existsZoneWithoutCollection)
  );

  const isLoading =
    isLoadingCamerasCount ||
    isLoadingZonesCount ||
    isLoadingExistsZoneWithoutCollection ||
    isLoadingExistsDaysWithoutSchedule ||
    isLoadingUnlinkedCollections ||
    isLoadingCollectionsWithoutRevenue;

  const formatStartDate = (date) => {
    return date.toISOString().split("T")[0] + "T00:00";
  };

  const formatEndDate = (date) => {
    return date.toISOString().split("T")[0] + "T23:59";
  };

  const formatEndDateToday = (date) => {
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return date.toISOString().split("T")[0] + `T${hours}:${minutes}`;
  };

  const getDateParams = () => {
    const today = new Date();
    let weekAgo, monthAgo, startToday, startYesterday, endYesterday;

    switch (selectedPeriod) {
      case "week":
        weekAgo = new Date(today);
        weekAgo.setDate(today.getDate() - 7);
        weekAgo.setHours(0, 0, 0, 0);
        return {
          start_date: formatStartDate(weekAgo),
          end_date: formatEndDate(today),
        };
      case "month":
        monthAgo = new Date(today);
        monthAgo.setDate(today.getDate() - 30);
        monthAgo.setHours(0, 0, 0, 0);
        return {
          start_date: formatStartDate(monthAgo),
          end_date: formatEndDate(today),
        };
      case "day":
        startToday = new Date(today);
        startToday.setHours(0, 0, 0, 0);
        return {
          start_date: formatStartDate(startToday),
          end_date: formatEndDateToday(today),
        };
      default: // yesterday
        if (lastRecord?.date) {
          const lastRecordDate = new Date(lastRecord.date);
          return {
            start_date: formatStartDate(lastRecordDate),
            end_date: formatEndDate(lastRecordDate),
          };
        }
        startYesterday = new Date(today);
        startYesterday.setDate(today.getDate() - 1);
        endYesterday = new Date(today);
        endYesterday.setDate(today.getDate() - 1);
        return {
          start_date: formatStartDate(startYesterday),
          end_date: formatEndDate(endYesterday),
        };
    }
  };

  const params = getDateParams();

  const { data: revenueKPIsData, isLoading: isLoadingRevenueKPIsData } =
    useHomeRevenueKPIsData(params.start_date, params.end_date);
  const { data: permanenceData, isLoading: isLoadingPermanenceData } =
    useFlowDetectionsProcessData(
      params.start_date,
      params.end_date,
      ["all"],
      ["all"],
      !isLastRecordPending
    );

  const handleCloseModal = async () => {
    await postShouldNeverShowTutorial();
    localStorage.setItem("should_never_show_tutorial", true);
    setShouldShowTutorialBool(false);
  };

  return (
    <ConfigProvider theme={{ components: { Card: { headerBg: "#d0d6e2" } } }}>
      <Modal
        open={isModalOpen && shouldShowTutorialBool}
        onCancel={() => setIsModalOpen(false)}
        onOk={() => {
          setIsModalOpen(false);
          setIsZeroPhaseTutorialOpen(true);
        }}
        okText="Iniciar tutorial"
        cancelText="Pular tutorial"
        cancelButtonProps={{ danger: true, onClick: handleCloseModal }}>
        <Title level={1}>Bem-vindo!</Title>
        <Text>
          Essa é a plataforma de monitoramento de fluxo de pessoas chamada
          Ouran! Para iniciar, adicione{" "}
          <Link href="/zones_manager">câmeras</Link>. Depois, relacione câmeras
          a zonas para análise detalhada.
        </Text>
      </Modal>

      <Flex
        style={{ width: "100%", minHeight: "100vh", gap: "10px" }}
        justify="flex-start"
        align="stretch"
        vertical>
        {isLoading ? (
          <Spin size="large" style={{ margin: "auto" }} />
        ) : (
          <Flex
            justify="flex-start"
            align="stretch"
            vertical
            gap="10px"
            style={{ width: "100%", padding: "0 2rem" }}>
            {!hasCameras && (
              <Warning
                title={warnings[0].title}
                description={warnings[0].description}
                about={warnings[0].about}
                link={warnings[0].link}
              />
            )}
            {!hasZones && (
              <Warning
                title={warnings[1].title}
                description={warnings[1].description}
                about={warnings[1].about}
                link={warnings[1].link}
              />
            )}
            {existsZoneWithoutCollection && (
              <Warning
                title={warnings[2].title}
                description={warnings[2].description}
                about={warnings[2].about}
                link={warnings[2].link}
              />
            )}
            {existsDaysWithoutSchedule && (
              <Warning
                title={warnings[3].title}
                description={warnings[3].description}
                about={warnings[3].about}
                link={warnings[3].link}
              />
            )}
            {unlinkedCollections?.total_nao_vinculadas > 0 && (
              <Warning
                title={warnings[4].title}
                description={warnings[4].description}
                about={warnings[4].about}
                link={warnings[4].link}
                colecoes={unlinkedCollections.colecoes_nao_vinculadas}
              />
            )}
            {collectionsWithoutRevenue?.total_sem_faturamento > 0 && (
              <Warning
                title={warnings[5].title}
                description={warnings[5].description}
                about={warnings[5].about}
                link={warnings[5].link}
                colecoes={collectionsWithoutRevenue.colecoes_sem_faturamento}
              />
            )}
          </Flex>
        )}

        {hasZones && hasCameras && !existsDaysWithoutSchedule && !isLoading && (
          <HomeDashboard
            selectedPeriod={selectedPeriod}
            setSelectedPeriod={setSelectedPeriod}
            revenueKPIsData={revenueKPIsData?.geral}
            isLoadingRevenueKPIsData={isLoadingRevenueKPIsData}
            permanenceData={permanenceData}
            isLoadingPermanenceData={isLoadingPermanenceData}
            startDate={params.start_date}
            endDate={params.end_date}
          />
        )}
      </Flex>
    </ConfigProvider>
  );
}
